import { ChangeDetectorRef, Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { Apollo } from 'apollo-angular';
import 'brace';
import 'brace/mode/json';
import 'brace/mode/text';
import 'brace/theme/github';
import { Buffer } from 'buffer';
import { TableLazyLoadEvent } from 'primeng/table';
import { NXDataSourcePrime } from '../../services/dataSource/NXDataSourcePrime';
import { EventSubscriptionsDataSource } from './eventSubscriptionsDataSource';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { EditDialogService } from '../editDialog/editDialogService';
import { LoadingService } from '../../services/loading.service';
import { ToastService } from '../../services/toast/toast.service';
import { SubscriptionDialogComponent } from '../subscription-dialog/subscription-dialog.component';
import { ConfirmDialogService } from '../confirmDialog/confirmDialogService';
import { ConfirmDialogEventType, ConfirmDialogSeverity } from '../confirmDialog/confirmDialogComponent';
import { TranslateService } from '@ngx-translate/core';
import { EventSubscriptionService } from '../../services/eventSubscription/eventSubscriptionService';
import { take } from 'rxjs';
import { MenuItem } from 'primeng/api';
import { SubscriptionSecretDialogComponent } from '../subscription-secret-dialog/subscription-dialog.component';

@Component({
  selector: 'app-subscriptions',
  templateUrl: './subscriptions.component.html',
  styleUrls: ['./subscriptions.component.scss']
})
export class SubscriptionsComponent implements OnInit, OnDestroy {

  dialogRef: DynamicDialogRef | undefined;

  public sortProperty: string;
  public defaultSortProperty: string = 'id';
  public sortDirection: string;
  public defaultSortDirection: string = 'asc';
  public pageNumber = 0;
  public pageSize = 15;
  public sortModel = { label: 'Keine Sortierung', fieldName: 'id', direction: "asc" };
  public sortOptions = [
    { label: 'coreModuleModule.subscriptionsComponent.list.sorting.noSorting', fieldName: 'id', direction: "asc" },
    { label: 'coreModuleModule.subscriptionsComponent.list.sorting.lastTriggeredDateTimeDesc', fieldName: 'lastTriggeredDateTime', direction: "desc" },
    { label: 'coreModuleModule.subscriptionsComponent.list.sorting.lastTriggeredDateTimeAsc', fieldName: 'lastTriggeredDateTime', direction: "asc" }
  ];

  public dataSource: EventSubscriptionsDataSource;
  public loading = false;
  public menuItems: MenuItem[] = [];

  public customHeadersArray: Array<{ name: string, value: string }>;

  // ace config so that it only takes the hight of the content
  editorConfig = {
    maxLines: Infinity,
    highlightActiveLine: false,
    highlightGutterLine: false
  }

  constructor(
    private apollo: Apollo,
    @Inject('env') public environment: any,
    private cdRef: ChangeDetectorRef,
    public dialogService: EditDialogService,
    private loadingService: LoadingService,
    private toastService: ToastService,
    private confirmDialogService: ConfirmDialogService,
    private translate: TranslateService,
    private eventSubscriptionService: EventSubscriptionService
  ) {
  }

  ngOnInit(): void {
    console.log('SubscriptionsComponent ngOnInit');
    this.dataSource = new EventSubscriptionsDataSource(this.apollo, this.environment, this);
    this.dataSource.loading.subscribe((loading) => {
      this.loading = loading;
    });
    this.dataSource.loadEvents({
      type: 'WEBHOOK',
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      sortProperty: this.sortProperty ? this.sortProperty : this.defaultSortProperty,
      sortDirection: this.sortDirection ? this.sortDirection : this.defaultSortDirection,
    });
  }

  refetchEvents(): void {
    this.dataSource.loadEvents({
      type: 'WEBHOOK',
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      sortProperty: this.sortProperty ? this.sortProperty : this.defaultSortProperty,
      sortDirection: this.sortDirection ? this.sortDirection : this.defaultSortDirection,
    });
  }

  ngOnDestroy(): void {
    this.dataSource.disconnect();
  }

  loadData(event: TableLazyLoadEvent) {
    this.loading = true;

    // sorting
    if (event?.sortField !== undefined && typeof event?.sortField == 'string') {
      this.sortProperty = event.sortField;
    }
    if (event?.sortOrder !== undefined) {
      this.sortDirection = event.sortOrder === 1 ? 'asc' : 'desc';
    }

    // pagination
    this.pageNumber = event?.first !== undefined ? event.first / event.rows : 0;
    this.pageSize = event?.rows !== undefined ? event.rows : 15;

    this.refetchEvents();
  }

  sort(event: any) {
    if (event?.value?.direction && event?.value?.fieldName) {
      this.sortProperty = event.value.fieldName;
      this.sortDirection = event.value.direction;
    } else {
      this.sortProperty = this.defaultSortProperty;
      this.sortDirection = this.defaultSortDirection;
    }
    this.cdRef.detectChanges();
  }

  translateSortDirectionToSortOrder(sortDirection: string): number {
    return NXDataSourcePrime.translateSortDirectionToSortOrder(sortDirection);
  }

  copyToClipboard(stringToCopy: string): void {
    navigator.clipboard.writeText(stringToCopy);
  }

  editSubscription(): void {
    this.dialogRef = this.dialogService.open(SubscriptionDialogComponent, {
      header: 'coreModuleModule.subscriptionsComponent.updateSubscriptionDialogHeader',
      data: this.dataSource?.selectedEventSubscription
    });
    this.dialogRef.onClose.subscribe((result) => {
      // result is true or false, indiciating if it should refetch
      if (result) {
        this.refetchEvents();
        this.eventSubscriptionService.getEventSubscriptionById(this.dataSource?.selectedEventSubscription?.id).pipe(
          take(1)
        ).subscribe((eventSubscription) => {
          this.dataSource.selectedEventSubscription = eventSubscription as any;
        });
        this.loadingService.setLoading(false);
        this.toastService.addToastWithMessage('coreModuleModule.subscriptionsComponent.subscriptionUpdatedToastMessage');
      }
    });
  }

  deleteSubscription(): void {
    this.dialogRef = this.confirmDialogService.open({
      message: this.translate.instant(
        'coreModuleModule.subscriptionsComponent.details.deleteSubscriptionDialogMessage',
        { subscriptionId: this.dataSource?.selectedEventSubscription?.id }
      ),
      severity: ConfirmDialogSeverity.WARN,
      header: 'coreModuleModule.subscriptionsComponent.details.deleteSubscriptionDialogHeader',
    });
    this.dialogRef.onClose.subscribe((result: ConfirmDialogEventType) => {
      if (result === ConfirmDialogEventType.CONFIRM) {
        this.loadingService.setLoading(true);
        this.eventSubscriptionService.delete(this.dataSource?.selectedEventSubscription?.id, () => {
          this.refetchEvents();
          this.loadingService.setLoading(false);
          this.toastService.addToastWithMessage('coreModuleModule.subscriptionsComponent.details.subscriptionDeletedToastMessage');
        });
      };
    })
  }

  createSubscription(): void {
    this.dialogRef = this.dialogService.open(SubscriptionDialogComponent, {
      header: 'coreModuleModule.subscriptionsComponent.createSubscriptionDialogHeader',
    });
    this.dialogRef.onClose.subscribe((result) => {
      // result is true or false, indiciating if it should refetch
      if (result) {
        this.refetchEvents();
        this.eventSubscriptionService.getEventSubscriptionById(result).pipe(
          take(1)
        ).subscribe((eventSubscription) => {
          this.dataSource.selectedEventSubscription = eventSubscription as any;
          this.setMenuItems();
        });
        this.loadingService.setLoading(false);
        this.toastService.addToastWithMessage('coreModuleModule.subscriptionsComponent.subscriptionCreatedToastMessage');
      }
    });
  }

  sendTestEvent(): void {
    this.dialogRef = this.confirmDialogService.open({
      message: 'coreModuleModule.subscriptionsComponent.details.sendTestEventDialogMessage',
      severity: ConfirmDialogSeverity.DEFAULT,
      header: 'coreModuleModule.subscriptionsComponent.details.sendTestEventDialogHeader',
    });
    this.dialogRef.onClose.subscribe((result: ConfirmDialogEventType) => {
      if (result === ConfirmDialogEventType.CONFIRM) {
        this.loadingService.setLoading(true);
        this.eventSubscriptionService.sendTestEvent((result: any) => {
          this.loadingService.setLoading(false);
          this.toastService.addToastWithMessage('coreModuleModule.subscriptionsComponent.details.testEventSentToastMessage');
        });
      };
    })
  }

  setSecret(): void {
    this.dialogRef = this.dialogService.open(SubscriptionSecretDialogComponent, {
      header: 'coreModuleModule.subscriptionsComponent.setSecretDialogHeader',
      data: this.dataSource?.selectedEventSubscription
    });
    this.dialogRef.onClose.subscribe((result) => {
      // result is true or false, indiciating if it should refetch
      if (result) {
        this.refetchEvents();
        this.eventSubscriptionService.getEventSubscriptionById(this.dataSource?.selectedEventSubscription?.id).pipe(
          take(1)
        ).subscribe((eventSubscription) => {
          this.dataSource.selectedEventSubscription = eventSubscription as any;
          this.setMenuItems();
        });
        this.loadingService.setLoading(false);
        this.toastService.addToastWithMessage('coreModuleModule.subscriptionsComponent.secretSetToastMessage');
      }
    });
  }

  deleteSecret(): void {
    this.dialogRef = this.confirmDialogService.open({
      message: this.translate.instant(
        'coreModuleModule.subscriptionsComponent.details.deleteSecretDialogMessage',
        { subscriptionId: this.dataSource?.selectedEventSubscription?.id }
      ),
      severity: ConfirmDialogSeverity.WARN,
      header: 'coreModuleModule.subscriptionsComponent.details.deleteSecretDialogHeader',
    });
    this.dialogRef.onClose.subscribe((result: ConfirmDialogEventType) => {
      if (result === ConfirmDialogEventType.CONFIRM) {
        this.loadingService.setLoading(true);
        this.eventSubscriptionService.deleteSecret(this.dataSource?.selectedEventSubscription?.id, () => {
          this.eventSubscriptionService.getEventSubscriptionById(this.dataSource?.selectedEventSubscription?.id).pipe(
            take(1)
          ).subscribe((eventSubscription) => {
            this.dataSource.selectedEventSubscription = eventSubscription as any;
            this.setMenuItems();
          });
          this.loadingService.setLoading(false);
          this.toastService.addToastWithMessage('coreModuleModule.subscriptionsComponent.details.secretDeletedToastMessage');
        });
      };
    })
  }

  setMenuItems(): void {
    this.menuItems = [];
    this.menuItems.push({
      label: this.translate.instant('coreModuleModule.subscriptionsComponent.details.menu.editSubscription'),
      disabled: false,
      command: () => {
        this.editSubscription();
      }
    });

    this.menuItems.push({
      label: this.translate.instant('coreModuleModule.subscriptionsComponent.details.menu.deleteSubscription'),
      disabled: false,
      command: () => {
        this.deleteSubscription();
      }
    });

    this.menuItems.push({
      label: this.translate.instant('coreModuleModule.subscriptionsComponent.details.menu.setSecret'),
      disabled: false,
      command: () => {
        this.setSecret();
      }
    });

    this.menuItems.push({
      label: this.translate.instant('coreModuleModule.subscriptionsComponent.details.menu.unsetSecret'),
      disabled: this.dataSource?.selectedEventSubscription?.secretHash === undefined || this.dataSource?.selectedEventSubscription?.secretHash === null,
      command: () => {
        this.deleteSecret();
      }
    });

    this.menuItems.push({
      label: this.translate.instant('coreModuleModule.subscriptionsComponent.details.menu.sendTestEvent'),
      disabled: false,
      command: () => {
        this.sendTestEvent();
      }
    });
  }

  // method converts an object that looks like this:
  // {
  //     "x-api-key": "xxx",
  //     "x-secret-hash": "xxx",
  //     "Content-Type": "application/json"
  // }
  // to an array that looks like this:
  // [
  //     { name: "x-api-key", value: "xxx" },
  //     { name: "x-secret-hash", value: "xxx" },
  //     { name: "Content-Type", value: "application/json" }
  // ]


}
